import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const AddressCategorizationTool = ({ address, onCategorize }) => {
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');

  const categoryOptions = [
    { value: 'exchange', label: 'Exchange' },
    { value: 'mixer', label: 'Mixing Service' },
    { value: 'criminal', label: 'Criminal Entity' },
    { value: 'darknet', label: 'Darknet Marketplace' },
    { value: 'ransomware', label: 'Ransomware' },
    { value: 'gambling', label: 'Gambling Platform' },
    { value: 'defi', label: 'DeFi Protocol' },
    { value: 'wallet', label: 'Personal Wallet' },
    { value: 'merchant', label: 'Merchant/Business' },
    { value: 'unknown', label: 'Unknown/Unclassified' }
  ];

  const subcategoryOptions = {
    exchange: [
      { value: 'centralized', label: 'Centralized Exchange' },
      { value: 'dex', label: 'Decentralized Exchange' },
      { value: 'otc', label: 'OTC Trading' }
    ],
    mixer: [
      { value: 'tumbler', label: 'Bitcoin Tumbler' },
      { value: 'coinjoin', label: 'CoinJoin Service' },
      { value: 'privacy_coin', label: 'Privacy Coin Bridge' }
    ],
    criminal: [
      { value: 'theft', label: 'Theft/Hack' },
      { value: 'fraud', label: 'Fraud Scheme' },
      { value: 'money_laundering', label: 'Money Laundering' }
    ]
  };

  const handleAddTag = () => {
    if (newTag?.trim() && !tags?.includes(newTag?.trim())) {
      setTags([...tags, newTag?.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags?.filter(tag => tag !== tagToRemove));
  };

  const handleSave = () => {
    const categorization = {
      address,
      category,
      subcategory,
      notes,
      tags,
      timestamp: new Date()?.toISOString(),
      analyst: 'Agent Smith'
    };
    
    onCategorize?.(categorization);
    
    // Reset form
    setCategory('');
    setSubcategory('');
    setNotes('');
    setTags([]);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-forensic-card">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Tag" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-text-primary">Address Categorization</h3>
      </div>
      <div className="space-y-4">
        <div className="p-3 bg-muted rounded-lg">
          <div className="text-sm text-text-secondary mb-1">Target Address</div>
          <div className="font-data text-sm text-text-primary break-all">{address}</div>
        </div>

        <Select
          label="Primary Category"
          placeholder="Select address category"
          options={categoryOptions}
          value={category}
          onChange={setCategory}
          required
        />

        {category && subcategoryOptions?.[category] && (
          <Select
            label="Subcategory"
            placeholder="Select subcategory"
            options={subcategoryOptions?.[category]}
            value={subcategory}
            onChange={setSubcategory}
          />
        )}

        <div>
          <Input
            label="Tags"
            type="text"
            placeholder="Add custom tags"
            value={newTag}
            onChange={(e) => setNewTag(e?.target?.value)}
            description="Press Enter or click Add to include tags"
            onKeyPress={(e) => e?.key === 'Enter' && handleAddTag()}
          />
          <div className="flex items-center space-x-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddTag}
              disabled={!newTag?.trim()}
            >
              Add Tag
            </Button>
          </div>
          
          {tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {tags?.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-red-200"
                  >
                    <Icon name="X" size={12} color="currentColor" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <Input
          label="Investigation Notes"
          type="textarea"
          placeholder="Add detailed notes about this address classification..."
          value={notes}
          onChange={(e) => setNotes(e?.target?.value)}
          description="Include evidence, sources, and reasoning for this categorization"
          className="min-h-24"
        />

        <div className="flex items-center space-x-3 pt-4">
          <Button
            onClick={handleSave}
            disabled={!category}
            iconName="Save"
            iconPosition="left"
          >
            Save Categorization
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setCategory('');
              setSubcategory('');
              setNotes('');
              setTags([]);
            }}
          >
            Clear Form
          </Button>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Info" size={16} color="var(--color-text-secondary)" />
          <span className="text-sm font-medium text-text-secondary">Classification Guidelines</span>
        </div>
        <ul className="text-xs text-text-secondary space-y-1">
          <li>• Exchange: Addresses belonging to cryptocurrency exchanges</li>
          <li>• Mixer: Services designed to obscure transaction trails</li>
          <li>• Criminal: Addresses associated with illegal activities</li>
          <li>• Darknet: Marketplace addresses from dark web platforms</li>
          <li>• Ransomware: Addresses used for ransom payments</li>
        </ul>
      </div>
    </div>
  );
};

export default AddressCategorizationTool;